import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, finalize, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { SbkInvestDataService } from '../sbk-invest.service';
import {
  fetchUnitTrustAccounts, fetchUnitTrustAccountsFailure, fetchUnitTrustAccountsSuccess,
  getUnitTrustAccountStatement, getUnitTrustAccountStatementFailure,
  getUnitTrustAccountStatementSuccess, fetchUnitTrustAccountTransactions,
  fetchUnitTrustAccountTransactionsFailure, fetchUnitTrustAccountTransactionsSuccess,
  openUnitTrustFundAccount, openUnitTrustFundAccountFailure, openUnitTrustFundAccountSuccess,
  openSavingsAccount, openSavingsAccountFailure, openSavingsAccountSuccess, withdrawFromUnitTrustAccount,
  withdrawFromUnitTrustAccountFailure, withdrawFromUnitTrustAccountSuccess,
  getSavingsAccountParams,
  getSavingsAccountParamsSuccess,
  getSavingsAccountParamsFailure,
  fetchUnitTrustFactSheets,
  fetchUnitTrustFactSheetsFailure,
  fetchUnitTrustFactSheetsSuccess
} from './actions';
import { CustomNotificationService, LoaderService, setLoading } from '@sbk/shared';
import { Store } from '@ngrx/store';

export const buildSavingsAccountFailureMessage = (error: any): string => {
  if (typeof error === 'string' && error.trim()) {
    return error;
  }

  const backendMessage = error?.message || error?.responseMessage || error?.error?.message || error?.error?.responseMessage;
  if (typeof backendMessage === 'string' && backendMessage.trim()) {
    return backendMessage;
  }

  if (typeof error?.statusText === 'string' && error.statusText.trim()) {
    return error.statusText;
  }

  return 'Account opening failed';
};

export const buildSavingsAccountSuccessNotification = (accountDetails: any) => {
  const isSecondaryAccount = accountDetails?.journeyType === 'secondary';
  return {
    title: isSecondaryAccount
      ? 'Secondary Account Opening Request Submitted'
      : 'Savings Account Opening Request Submitted',
    message: isSecondaryAccount
      ? 'Your secondary account opening request was submitted successfully'
      : 'Your savings account opening request was submitted successfully',
  };
};

@Injectable()
export class InvestEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  /**
   * Open Savings Account
  */
  openSavingsAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(openSavingsAccount),
      tap(() => this.store.dispatch(setLoading({ key: 'openSavingsAccount', loading: true }))),
      switchMap((action) => {
        return this.dataService.openSavingsAccount(action.payload).pipe(
          map((response) => {
            this.store.dispatch(setLoading({ key: 'openSavingsAccount', loading: false }));
            const accountDetails = response && typeof response === 'object'
              ? { ...response, journeyType: action.journeyType }
              : { response, journeyType: action.journeyType };

            if (response?.status === 200) {
              return openSavingsAccountSuccess({ accountDetails });
            } else {
              return openSavingsAccountFailure({ error: buildSavingsAccountFailureMessage(response) });
            }
          }),
          catchError((error) => {
            this.store.dispatch(setLoading({ key: 'openSavingsAccount', loading: false }));
            return of(openSavingsAccountFailure({ error: buildSavingsAccountFailureMessage(error) }));
          })
        );
      })
    );
  });

  openSavingsAccountSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(openSavingsAccountSuccess),
        tap((action) => {
          const notification = buildSavingsAccountSuccessNotification(action.accountDetails);
          this.customNotificationService.showNotification(notification.title, notification.message, 'success');
        }),
      );
    },
    { dispatch: false }
  );

  openSavingsAccountFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(openSavingsAccountFailure),
        tap((action) => {
          this.customNotificationService.showNotification(
            'Account Opening Failed', `${action.error}`, 'error');
        }),
      );
    },
    { dispatch: false }
  );

  /**
   * Get Savings Account Params
  */
  getSavingsAccountParams$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getSavingsAccountParams),
      tap(() => this.store.dispatch(setLoading({ key: 'getSavingsAccountParams', loading: true }))),
      switchMap(() =>
        this.dataService.getSavingsAccountParams().pipe(
          map((response) => {
            this.store.dispatch(setLoading({ key: 'getSavingsAccountParams', loading: false }));
            return getSavingsAccountParamsSuccess({ savingsAccountParams: response.object });
          }),
          catchError((error) => {
            this.store.dispatch(setLoading({ key: 'getSavingsAccountParams', loading: false }));
            return of(getSavingsAccountParamsFailure({ error: error?.statusText }));
          })
        )
      )
    )
  );

  getSavingsAccountParamsFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(getSavingsAccountParamsFailure),
        tap((action) => {
          this.customNotificationService.showNotification(
            'Failed to Fetch Savings Account Params', `${action.error}`, 'error');
        }),
      );
    },
    { dispatch: false }
  );

  /**
   * Open Unit Trust Account (generic)
  */

  private runOpenUnitTrustFlow(action: { payload: any; extraDetails?: any; fundShareClassCode: string; fundName?: string }) {
    const isRegisteredPayload = {
      Channel: 'OMNI'
    };

    const buildSubAccountDetails = (clientIdentifier: string) => ({
      Currency: action.extraDetails?.currency,
      AccountNumber: action.extraDetails?.accountId,
      SubAccountName: action.extraDetails?.investmentName,
      ClientIdentifier: clientIdentifier,
      ClientName: action.extraDetails?.accountName,
      Channel: 'OMNI'
    });

    const joinFund = (clientIdentifier: string) => {
      const joinFundPayload = {
        FundShareClassCode: action.fundShareClassCode,
        ClientIdentifier: clientIdentifier,
        Channel: 'OMNI',
        ClientFundName: action.extraDetails?.investmentName
      };

      return this.dataService.joinFund(joinFundPayload).pipe(
        map((joinFundResponse: any) => {
          if (joinFundResponse?.ResponseCode === '200') {
            return openUnitTrustFundAccountSuccess({
              accountDetails: joinFundResponse,
              fundShareClassCode: action.fundShareClassCode,
              fundName: action.fundName,
            });
          }

          return openUnitTrustFundAccountFailure({
            error: joinFundResponse?.Message || 'Join fund operation failed',
            fundShareClassCode: action.fundShareClassCode,
            fundName: action.fundName,
          });
        }),
        catchError((error) => of(openUnitTrustFundAccountFailure({
          error: error?.statusText || 'An error occurred during fund joining',
          fundShareClassCode: action.fundShareClassCode,
          fundName: action.fundName,
        })))
      );
    };

    const createSubAccountAndJoin = (clientIdentifier?: string) => {
      if (!clientIdentifier) {
        return of(openUnitTrustFundAccountFailure({
          error: 'Client identifier is missing',
          fundShareClassCode: action.fundShareClassCode,
          fundName: action.fundName,
        }));
      }

      const subAccountDetails = buildSubAccountDetails(clientIdentifier);

      return this.dataService.createSubAccount(subAccountDetails).pipe(
        switchMap((subAccountResponse: any) => {
          if (subAccountResponse?.ResponseCode === '200') {
            return joinFund(subAccountResponse.ClientIdentifier);
          }

          return of(openUnitTrustFundAccountFailure({
            error: subAccountResponse?.Message || 'Sub-account creation failed',
            fundShareClassCode: action.fundShareClassCode,
            fundName: action.fundName,
          }));
        }),
        catchError((error) => of(openUnitTrustFundAccountFailure({
          error: error?.statusText || 'An error occurred during sub-account creation',
          fundShareClassCode: action.fundShareClassCode,
          fundName: action.fundName,
        })))
      );
    };

    return this.dataService.checkIfClientIsRegistered(isRegisteredPayload).pipe(
      switchMap((isRegisteredResponse: any) => {
        const investmentGroupAccount = isRegisteredResponse?.subAccountsList?.find(
          (account: any) => account.Type === 'INVESTMENT_GROUP_ACCOUNT'
        );

        if (isRegisteredResponse?.isRegistered) {
          return createSubAccountAndJoin(investmentGroupAccount?.ClientIdentifier);
        }

        return this.dataService.openUnitTrustFundAccount(action.payload).pipe(
          switchMap((response: any) => {
            if (response?.ResponseCode === '200') {
              return createSubAccountAndJoin(response?.ClientIdentifier);
            }

            return of(openUnitTrustFundAccountFailure({
              error: response?.Message || 'Unit trust account creation failed',
              fundShareClassCode: action.fundShareClassCode,
              fundName: action.fundName,
            }));
          }),
          catchError((error) => of(openUnitTrustFundAccountFailure({
            error: error?.statusText || 'An error occurred during account creation',
            fundShareClassCode: action.fundShareClassCode,
            fundName: action.fundName,
          })))
        );
      }),
      catchError((error) => of(openUnitTrustFundAccountFailure({
        error: error?.statusText || 'An error occurred while checking registration status',
        fundShareClassCode: action.fundShareClassCode,
        fundName: action.fundName,
      })))
    );
  }

  openUnitTrustFundAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(openUnitTrustFundAccount),
      mergeMap((action) => {
        this.store.dispatch(setLoading({ key: 'openUnitTrustFundAccount', loading: true }));

        return this.runOpenUnitTrustFlow(action).pipe(
          finalize(() => {
            this.store.dispatch(setLoading({ key: 'openUnitTrustFundAccount', loading: false }));
          })
        );
      })
    );
  });

  openUnitTrustFundAccountSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(openUnitTrustFundAccountSuccess),
        tap((action) => {
          const fundLabel = action.fundName || action.fundShareClassCode || 'Unit Trust';
          this.customNotificationService.showNotification(
            'Unit Trust Account Opening Request Submitted',
            `Your ${fundLabel} account opening request was submitted successfully`,
            'success'
          );
        }),
      );
    },
    { dispatch: false }
  );

  openUnitTrustFundAccountFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(openUnitTrustFundAccountFailure),
        tap((action) => {
          const fundLabel = action.fundName || action.fundShareClassCode || 'Unit Trust';
          this.customNotificationService.showNotification(
            `${fundLabel} Account Opening Failed`, `${action.error}`, 'error');
        }),
      );
    },
    { dispatch: false }
  );

  /**
   * Get unit trust account transactions
   */

  fetchUnitTrustAccountTransactions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fetchUnitTrustAccountTransactions),
      mergeMap((action) => {
        this.loaderService.setLoadingState(true);

        return this.dataService.fetchUnitTrustAccountTransactions(action.payload).pipe(
          map((response: any) => {
            return fetchUnitTrustAccountTransactionsSuccess({ transactions: response });
          }),
          catchError((error) => of(fetchUnitTrustAccountTransactionsFailure({ error: error?.statusText }))),
          finalize(() => {
            this.loaderService.setLoadingState(false);
          })
        );
      })
    );
  });

  fetchUnitTrustAccountTransactionsFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fetchUnitTrustAccountTransactionsFailure),
        tap((action) => {
          this.customNotificationService.showNotification(
            'Failed to Fetch Transactions', `${action.error}`, 'error');
        }),
      );
    },
    { dispatch: false }
  );

  /**
   * Get statement
   */
  getUnitTrustAccountStatement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getUnitTrustAccountStatement),
      tap(() => this.store.dispatch(setLoading({ key: 'getUnitTrustAccountStatement', loading: true }))),
      mergeMap((action) =>
        this.dataService.getUnitTrustAccountStatement(action.payload).pipe(
          map((response) => {
            this.store.dispatch(setLoading({ key: 'getUnitTrustAccountStatement', loading: false }));
            return getUnitTrustAccountStatementSuccess({ statement: response });
          }),
          catchError((error) => {
            this.store.dispatch(setLoading({ key: 'getUnitTrustAccountStatement', loading: false }));
            return of(getUnitTrustAccountStatementFailure({ error: error?.statusText }));
          })
        )
      )
    )
  );

  getUnitTrustAccountStatementSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(getUnitTrustAccountStatementSuccess),
        tap(() => {
          this.customNotificationService.showNotification(
            'Statement Request Submitted', `Your statement request has been submitted. You will get the statement on your email shortly.`, 'success');
        }),
      );
    },
    { dispatch: false }
  );

  getUnitTrustAccountStatementFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(getUnitTrustAccountStatementFailure),
        tap((action) => {
          this.customNotificationService.showNotification(
            'Unable to Get your Statement', `${action.error}`, 'error');
        }),
      );
    },
    { dispatch: false }
  );


  /**
   * Withdraw from Unit Trust Account
  */
  withdrawFromUnitTrustAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(withdrawFromUnitTrustAccount),
      mergeMap((action) => {
        this.loaderService.setLoadingState(true);

        return this.dataService.withdrawFromUnitTrustAccount(action.payload).pipe(
          map((response: any) => {
            return withdrawFromUnitTrustAccountSuccess({ accountDetails: response });
          }),
          catchError((error) => of(withdrawFromUnitTrustAccountFailure({ error: error?.statusText }))),
          finalize(() => {
            this.loaderService.setLoadingState(false);
          })
        );
      })
    );
  });

  withdrawFromUnitTrustAccountSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(withdrawFromUnitTrustAccountSuccess),
        tap(() => {
          this.customNotificationService.showNotification(
            'Withdrawal Request Submitted', `Your unit trust withdrawal request has been submitted successfully`, 'success');
        }),
      );
    },
    { dispatch: false }
  );

  withdrawFromUnitTrustAccountFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(withdrawFromUnitTrustAccountFailure),
        tap((action) => {
          this.customNotificationService.showNotification(
            'Withdrawal Failed', `${action.error}`, 'error');
        }),
      );
    },
    { dispatch: false }
  );

  /**
   * Fetch unit trust accounts
   */
  getUnitTrustAccounts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fetchUnitTrustAccounts),
      mergeMap((action) => {
        this.loaderService.setLoadingState(true);

        return this.dataService.getUnitTrustAccounts(action.Channel).pipe(
          map((response: any) => {
            return fetchUnitTrustAccountsSuccess({ accounts: response });
          }),
          catchError((error) => of(fetchUnitTrustAccountsFailure({ error: error?.statusText }))),
          finalize(() => {
            this.loaderService.setLoadingState(false);
          })
        );
      })
    );
  });

  getUnitTrustAccountsFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fetchUnitTrustAccountsFailure),
        tap((action) => {
          this.customNotificationService.showNotification(
            'Unit Trust Accounts Fetch Failed', `${action.error}`, 'error');
        }),
      );
    },
    { dispatch: false }
  );

  /**
 * Fetch unit trust fact sheets
 */
  fetchUnitTrustFactSheets$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fetchUnitTrustFactSheets),
      mergeMap(() => {
        this.loaderService.setLoadingState(true);

        return this.dataService.getUnitTrustFactSheets().pipe(
          map((response: any) => {
            return fetchUnitTrustFactSheetsSuccess({ factSheets: response });
          }),
          catchError((error) => of(fetchUnitTrustFactSheetsFailure({ error: error?.statusText }))),
          finalize(() => {
            this.loaderService.setLoadingState(false);
          })
        );
      })
    );
  });

  fetchUnitTrustFactSheetsFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fetchUnitTrustFactSheetsFailure),
        tap((action) => {
          this.customNotificationService.showNotification(
            'Unit Trust Fact Sheets Fetch Failed', `${action.error}`, 'error');
        }),
      );
    },
    { dispatch: false }
  );

  constructor(
    private dataService: SbkInvestDataService,
    private customNotificationService: CustomNotificationService,
    private loaderService: LoaderService
  ) { }
}
